<template>
  <div class="container mt-4">

    <!-- TOP BAR -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">{{ $t('leaveManage.title') }}</h4>

      <!-- Admin only -->
      <button
  v-if="isAdmin"
  class="btn btn-sm text-white"
  style="background:#b03a45;"
  @click="goToMyLeaves"
>
  🧾 {{ $t('leaveManage.myLeaves') }}  
</button>
    </div>

    <div class="card shadow border-0">
      <div class="card-header text-white fw-bold" style="background: linear-gradient(195deg, #4b5563, #8d2e38);">
        <i class="material-icons me-2">description</i> {{ $t('leaveManage.subtitle') }}
      </div>

      <div class="p-3" v-if="!canModerate">
        <div class="alert alert-warning mb-0">
          {{ $t('leaveManage.noPermission') }}
        </div>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="text-uppercase small" style="background-color: #f8f9fa;">
            <tr>
              <th>{{ $t('leaveManage.table.employee') }}</th>
              <th>{{ $t('leaveManage.table.type') }}</th>
              <th>{{ $t('leaveManage.table.dates') }}</th>
              <th>{{ $t('leaveManage.table.days') }}</th>
              <th>{{ $t('leaveManage.table.reason') }}</th>
              <th>{{ $t('leaveManage.table.status') }}</th>
              <th>{{ $t('leaveManage.table.certificate') }}</th>
              <th>{{ $t('leaveManage.table.actions') }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="leave in leaves" :key="leave.id">
              <td>
                <div class="fw-semibold">
                  {{ leave.user?.firstname }} {{ leave.user?.lastname }}
                </div>
                <div class="text-muted small">{{ leave.user?.email || '' }}</div>
              </td>

              <td>{{ $t(`leaveRequests.${leave.type.toLowerCase()}`) || leave.type }}</td>
              <td>{{ leave.start_date }} → {{ leave.end_date }}</td>
              <td>{{ leave.days }}</td>
              <td>{{ leave.reason || '—' }}</td>

              <td>
                <span :class="['badge', statusClass(leave.status)]">
                  {{ leave.status }}
                </span>
              </td>

              <td>
                <a
                  v-if="leave.certificate_path"
                  :href="`http://127.0.0.1:8000/storage/${leave.certificate_path}`"
                  target="_blank"
                  class="btn btn-sm btn-outline-danger"
                >
                  {{ $t('leaveManage.actions.view') }}
                </a>
                <span v-else>—</span>
              </td>

              <td>
                <button
                  v-if="leave.status === 'Pending'"
                  class="btn btn-success btn-sm me-1"
                  :disabled="actioningId === leave.id"
                  @click="onApproveClick(leave.id)"
                >
                  ✔ {{ $t('leaveManage.actions.approve') }}
                </button>

                <button
                  v-if="leave.status === 'Pending'"
                  class="btn btn-danger btn-sm"
                  :disabled="actioningId === leave.id"
                  @click="onRejectClick(leave.id)"
                >
                  ✘ {{ $t('leaveManage.actions.reject') }}
                </button>
              </td>
            </tr>

            <tr v-if="leaves.length === 0">
              <td colspan="8" class="text-center text-muted py-4">
                {{ $t('leaveManage.noRequests') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from "vue-router"
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const leaves = ref([])
const actioningId = ref(null)
const supervisors = ref([])

// user localStorage (on garde ta structure)
const user = JSON.parse(localStorage.getItem('user') || '{}')
const userId = Number(user?.id || 0)
const roles = Array.isArray(user?.roles) ? user.roles : []
const isAdmin = computed(() => roles.includes('admin') || roles.includes('SuperAdmin'))

// est-ce que l'utilisateur est dans la liste des superviseurs ?
const isSupervisor = computed(() => supervisors.value.some(s => Number(s.id) === userId))

// autorisé à modérer ? (admin OU superviseur)
const canModerate = computed(() => isAdmin.value || isSupervisor.value)

// ✅ Button action: go to LeaveRequest (my leaves + create leave)
const goToMyLeaves = () => {
  router.push("/employee/leaves/request") // change si besoin
}

const fetchLeaves = async () => {
  try {
    const res = await axios.get('/employee/AllLeaves')
    leaves.value = res.data.leaves || []
  } catch (err) {
    toast.error(t('leaveManage.messages.loadError'))
  }
}

const fetchSupervisors = async () => {
  try {
    const { data } = await axios.get('/employee/leaves/supervisors')
    supervisors.value = data.supervisors || []
  } catch (err) {
    // soft error: on ne bloque pas la page
    console.warn('Failed to load supervisors', err)
  }
}

// gardes d’accès + actions
const onApproveClick = async (id) => {
  if (!canModerate.value) {
    toast.warning(t('leaveManage.messages.noApprovePermission'))
    return
  }
  if (!confirm(t('leaveManage.messages.confirmApprove'))) return
  await approveLeave(id)
}

const onRejectClick = async (id) => {
  if (!canModerate.value) {
    toast.warning("Vous n'êtes pas autorisé à rejeter des demandes.")
    return
  }
  if (!confirm('Reject this leave request?')) return
  await rejectLeave(id)
}

const approveLeave = async (id) => {
  actioningId.value = id
  try {
    await axios.patch(`/employee/leaves/approve/${id}`)
    toast.success('✅ Leave approved')
    await fetchLeaves()
  } catch (err) {
    if (err.response?.status === 403) {
      toast.error("⛔ Only the supervisor can approve this request.")
    } else {
      toast.error('❌ Approval failed')
    }
    console.error(err)
  } finally {
    actioningId.value = null
  }
}

const rejectLeave = async (id) => {
  actioningId.value = id
  try {
    await axios.patch(`/employee/leaves/reject/${id}`)
    toast.success('✅ Leave rejected')
    await fetchLeaves()
  } catch (err) {
    if (err.response?.status === 403) {
      toast.error("⛔ Only the supervisor can reject this request.")
    } else {
      toast.error('❌ Rejection failed')
    }
    console.error(err)
  } finally {
    actioningId.value = null
  }
}

const statusClass = (status) => {
  switch (status) {
    case 'Approved': return 'bg-success text-white'
    case 'Rejected': return 'bg-danger text-white'
    default: return 'bg-warning text-dark' // Pending
  }
}

const translateLeaveType = (type) => {
  const typeMap = {
    'Sick': t('leaveRequests.sick'),
    'Maternity': t('leaveRequests.maternity'),
    'Paternity': t('leaveRequests.paternity'),
    'Other': t('leaveRequests.other')
  }
  return typeMap[type] || type
}

onMounted(async () => {
  await Promise.all([fetchLeaves(), fetchSupervisors()])
  if (!canModerate.value) {
    toast.info("Read-only: you are not a supervisor.")
  }
})
</script>

<style scoped>
.badge {
  padding: 0.4em 0.8em;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 0.75rem;
}
</style>
